import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Monitor, Printer, PowerOff, RefreshCw, X } from 'lucide-react';
import { esWeb } from '@renderer/lib/entorno';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { cajaPorReiniciar } from '@renderer/lib/cajaPorReiniciar';
import { ENLACE_DESPERTAR_AGENTE } from '@shared/impresionEnlace';

/**
 * Sprint 156 — El aviso que faltaba el día que la caja amaneció sin imprimir.
 *
 * ## Qué pasó
 *
 * El 2026-09-26 el programa de impresión de la caja no arrancó con Windows. El
 * sistema hizo lo que siempre hizo: como nadie contestaba del otro lado, mandó
 * cada ticket a la ventana de imprimir de Chrome. En silencio. La dueña estuvo
 * media mañana cobrando sin entender por qué "no la deja imprimir", y el único
 * lugar del sistema que decía la verdad —"la caja no responde"— vivía dentro de
 * Configuración → Impresora, que es exactamente donde nadie está parado mientras
 * cobra.
 *
 * ## Las cuatro decisiones
 *
 * 1. **Sólo aparece si este negocio SÍ tiene impresora automática.** Un local que
 *    nunca instaló el programa imprime por el navegador a propósito: avisarle de
 *    algo que no perdió sería ruido, y el ruido es lo que enseña a ignorar avisos.
 * 2. **Barra fija, no globo ni modal.** Es una condición que dura, no una noticia:
 *    tiene que seguir ahí hasta que se resuelva. Y no puede robar el foco a media
 *    venta.
 * 3. **Trae la salida, no sólo el diagnóstico.** El botón abre el programa de la
 *    caja. Un aviso que informa sin dar salida termina en una llamada al soporte:
 *    es literalmente lo que pasó ese día.
 * 4. **Se puede posponer, pero vuelve.** "Ahora no" lo calla un rato; no lo apaga.
 *    Si se pudiera apagar, alguien lo apagaría y volveríamos al silencio.
 */

/** Cada cuánto se pregunta si la caja sigue viva. */
const MS_SONDEO = 30_000;

/** Cuánto calla el "Ahora no" antes de volver a aparecer. */
const MS_POSPUESTO = 10 * 60_000;

/*
  Sprint 165 — La barra va en la capa 45: DEBAJO de cualquier ventana (todas desde la 50). En la 75 tapaba el
  botón "Agregar al pedido" de la ventana del paquete (lo encontró el ensayo general del 24/10): con la
  impresora apagada, no se podía vender un paquete.
*/
export function AvisoImpresora() {
  const [caido, setCaido] = useState(false);
  const [pospuestoHasta, setPospuestoHasta] = useState(0);
  const [despertando, setDespertando] = useState(false);
  const [repartidas, setRepartidas] = useState(false);
  const [porReiniciar, setPorReiniciar] = useState(false);
  const [cajasNuevas, setCajasNuevas] = useState(0);
  const esDueno = useSessionStore((s) => s.sesion?.rol) === 'dueño';
  const navigate = useNavigate();

  const revisar = useCallback(async () => {
    const r = await window.api.impresion.estado();
    if (!r.ok) return;
    // Sin cajas emparejadas este negocio nunca dependió del agente: no hay nada
    // que avisar. Con al menos una, "ninguna contesta" sí es una pérdida real.
    const hayAgentes = r.data.agentes.length > 0;
    const algunoVivo = r.data.agentes.some((a) => a.conectado);
    setCaido(hayAgentes && !algunoVivo);
    setRepartidas(r.data.repartidas === true);
    setCajasNuevas(r.data.cajasNuevas ?? 0);

    // Sprint 156 — Si la caja tiene una versión que no sabe instalarse sola. Sólo le importa al
    // dueño, y la versión publicada sólo se le pregunta al servidor cuando hay algo que comparar.
    if (esDueno && r.data.agentes.some((a) => a.conectado)) {
      try {
        const v = await fetch('/api/agente/version');
        if (v.ok) {
          const { version } = (await v.json()) as { version: string | null };
          setPorReiniciar(cajaPorReiniciar(r.data.agentes, version));
        }
      } catch {
        /* sin el dato no se avisa: callar es mejor que pedir un reinicio que no hace falta */
      }
    } else {
      setPorReiniciar(false);
    }
  }, [esDueno]);

  useEffect(() => {
    // En la versión de escritorio el POS imprime directo: no hay agente ni caída
    // que avisar.
    if (!esWeb()) return;
    void revisar();
    const t = setInterval(() => void revisar(), MS_SONDEO);
    return () => clearInterval(t);
  }, [revisar]);

  // Cuando vuelve a haber impresora, el aviso se va solo y el "Ahora no" caduca:
  // la próxima caída tiene que poder avisar de inmediato.
  useEffect(() => {
    if (!caido) {
      setPospuestoHasta(0);
      setDespertando(false);
    }
  }, [caido]);

  if (Date.now() < pospuestoHasta) return null;

  /*
    Sprint 156 — Dos computadoras vivas se reparten los tickets al azar. Sólo al dueño: es el único
    que puede desconectar una, y a un mesero no le sirve de nada saberlo. Con la caja caída, manda
    el otro aviso: primero que salga papel.
  */
  if (!caido && repartidas && esDueno) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-[45] border-t-4 border-amber-500 bg-amber-100 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.12)]"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
          <Monitor className="h-7 w-7 shrink-0 text-amber-800" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-amber-900">
              Dos computadoras están recibiendo tus tickets
            </p>
            <p className="text-sm text-amber-900">
              Cada ticket sale sólo en una de las dos. Si una no está en el restaurante, esos
              tickets se imprimen donde nadie los ve.
            </p>
          </div>
          <button
            onClick={() => navigate('/settings?tab=impresora')}
            className="min-h-[44px] rounded-lg bg-amber-700 px-5 py-2 text-base font-bold text-white hover:bg-amber-800"
          >
            Ver cuáles son
          </button>
          <button
            onClick={() => setPospuestoHasta(Date.now() + MS_POSPUESTO)}
            aria-label="Ocultar este aviso un rato"
            className="min-h-[44px] rounded-lg border border-amber-700 px-4 py-2 text-base font-semibold text-amber-900 hover:bg-amber-200"
          >
            <X className="mr-1 inline h-4 w-4" aria-hidden />
            Ahora no
          </button>
        </div>
      </div>
    );
  }

  /*
    Sprint 158 (DT-161) — Una computadora nueva recibe los tickets del negocio. La laptop de pruebas
    que se llevó las comandas del Sprint 149 y los tickets del 26/09 se emparejó sin que nadie lo
    viera; ahora el dueño se entera hasta que diga "es mía" o la quite.
  */
  if (!caido && cajasNuevas > 0 && esDueno) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-[45] border-t-4 border-amber-500 bg-amber-100 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.12)]"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
          <Monitor className="h-7 w-7 shrink-0 text-amber-800" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-amber-900">
              Se conectó una computadora nueva a tus tickets
            </p>
            <p className="text-sm text-amber-900">
              Revisa que sea de tu negocio. Si no la reconoces, desconéctala.
            </p>
          </div>
          <button
            onClick={() => navigate('/settings?tab=impresora')}
            className="min-h-[44px] rounded-lg bg-amber-700 px-5 py-2 text-base font-bold text-white hover:bg-amber-800"
          >
            Revisar
          </button>
          <button
            onClick={() => setPospuestoHasta(Date.now() + MS_POSPUESTO)}
            aria-label="Ocultar este aviso un rato"
            className="min-h-[44px] rounded-lg border border-amber-700 px-4 py-2 text-base font-semibold text-amber-900 hover:bg-amber-200"
          >
            <X className="mr-1 inline h-4 w-4" aria-hidden />
            Ahora no
          </button>
        </div>
      </div>
    );
  }

  /*
    Sprint 156 — La única vez que hace falta tocar la computadora de la caja: para pasar a una
    versión que se instala sola. Se quita en cuanto la caja se actualiza; "Ahora no" lo calla un
    rato, no lo apaga. Un aviso de una sola vez se pierde con un clic sin leerlo.
  */
  if (!caido && porReiniciar && esDueno) {
    return (
      <div
        className="fixed inset-x-0 bottom-0 z-[45] border-t-4 border-sky-600 bg-sky-50 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.12)]"
        role="status"
        aria-live="polite"
      >
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
          <RefreshCw className="h-7 w-7 shrink-0 text-sky-800" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-sky-950">Hay una mejora para la impresora</p>
            <p className="text-sm text-sky-950">
              Hoy, al cerrar, <b>reinicia la computadora de la caja</b> (Inicio → Apagar →
              Reiniciar). Tarda 2 minutos y es la última vez: de ahí en adelante se actualiza sola,
              de madrugada.
            </p>
          </div>
          <button
            onClick={() => setPospuestoHasta(Date.now() + MS_POSPUESTO)}
            aria-label="Ocultar este aviso un rato"
            className="min-h-[44px] rounded-lg border border-sky-700 px-4 py-2 text-base font-semibold text-sky-950 hover:bg-sky-100"
          >
            <X className="mr-1 inline h-4 w-4" aria-hidden />
            Ahora no
          </button>
        </div>
      </div>
    );
  }

  if (!caido) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[45] border-t-4 border-amber-500 bg-amber-100 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.12)]"
      role="status"
      aria-live="polite"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
        <PowerOff className="h-7 w-7 shrink-0 text-amber-800" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-base font-bold text-amber-900">
            Los tickets no están saliendo por la impresora
          </p>
          <p className="text-sm text-amber-900">
            {despertando
              ? 'Abriendo el programa de impresión. Si Windows pregunta, permite abrir ' +
                '“RestaurantPOS Impresión”. En unos segundos este aviso desaparece solo.'
              : 'El programa de impresión de la caja está cerrado. Mientras tanto, cada ticket ' +
                'te va a abrir la ventana de imprimir.'}
          </p>
        </div>
        <button
          onClick={() => {
            setDespertando(true);
            /*
              Windows abre el programa registrado para este enlace. Si ya estuviera
              abierto, sólo trae su ventana al frente: no hay forma de hacer daño
              con un clic de más.

              Se dispara con un enlace suelto y no asignando `location.href`: en un
              equipo sin el programa (la tableta del mesero), Firefox reemplazaría
              la pantalla del POS por una de error y se perdería lo capturado.
            */
            const enlace = document.createElement('a');
            enlace.href = ENLACE_DESPERTAR_AGENTE;
            enlace.click();
          }}
          className="min-h-[44px] rounded-lg bg-amber-700 px-5 py-2 text-base font-bold text-white hover:bg-amber-800"
        >
          <Printer className="mr-2 inline h-5 w-5" aria-hidden />
          Encender la impresora
        </button>
        <button
          onClick={() => setPospuestoHasta(Date.now() + MS_POSPUESTO)}
          aria-label="Ocultar este aviso un rato"
          className="min-h-[44px] rounded-lg border border-amber-700 px-4 py-2 text-base font-semibold text-amber-900 hover:bg-amber-200"
        >
          <X className="mr-1 inline h-4 w-4" aria-hidden />
          Ahora no
        </button>
      </div>
    </div>
  );
}
